import React from 'react';

import type { ImageVideoHeroBannerProps } from 'Common/util/cmsComposer/HeroBanner';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import { P } from 'Elements/Typography';

import Grid, { Col } from 'Components/Grid';
import EventsBoxListVertical, { BoxListVerticalProps } from 'Components/Events/BoxList/Vertical';
import EventsBoxListHorizontal, {
    BoxListHorizontalProps
} from 'Components/Events/BoxList/Horizontal';
import Voucher, { SupportBlockLinkProps } from 'Components/SupportBlock/Link';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import LineListIcon, { LineListIconProps } from 'Components/List/LineList/Icon';

import VideoHeroBanner from 'Modules/HeroBanner/MakeWaves/Video';
import ImageHeroBanner from 'Modules/HeroBanner/MakeWaves/Image';
import CampusTour, { CampusTourProps } from 'Modules/OpenDays/Section/CampusTour';
import ContactUs, { ContactUsProps } from 'Modules/OpenDays/Section/ContactUs';

import { EventsSection } from './Landing.styled';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: ImageVideoHeroBannerProps;
    upcomingOpenDays: BoxListHorizontalProps;
    events: {
        intro: string;
        list: LineListIconProps;
    };
    eventBoxes: BoxListVerticalProps[];
    voucher: SupportBlockLinkProps;
    campusTour: CampusTourProps;
    contactUs: ContactUsProps;
}

const Landing: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        upcomingOpenDays,
        events,
        eventBoxes,
        voucher,
        campusTour,
        contactUs
    } = props;

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            {heroBanner.video && <VideoHeroBanner {...heroBanner.video} />}
            {heroBanner.image && <ImageHeroBanner {...heroBanner.image} />}
            <EventsSection id="upcoming-open-days" background paddingBottom={0.5}>
                <Grid>
                    <Col>
                        <EventsBoxListHorizontal {...upcomingOpenDays} />
                    </Col>
                </Grid>
            </EventsSection>
            <Section id="intro" background paddingTop={1}>
                <Grid>
                    <Col m={9} l={5}>
                        <P
                            size={{ large: '1.875rem' }}
                            spacing={{ large: { padding: '2rem 0 0 0' } }}
                        >
                            {events.intro}
                        </P>
                    </Col>
                    <Col l={[7, 13]}>
                        <LineListIcon {...events.list} />
                    </Col>
                </Grid>
            </Section>
            <Section id="events" background>
                <Grid style={{ gridRowGap: '1.5rem' }}>
                    {eventBoxes.map(box => {
                        return (
                            box.events.length > 0 && (
                                <Col m={6} key={box.heading}>
                                    <EventsBoxListVertical {...box} />
                                </Col>
                            )
                        );
                    })}
                </Grid>
            </Section>
            <Section id="voucher" background paddingTop={0.5} paddingBottom={1.5}>
                <Grid>
                    <Col l={[2, 12]} m={[3, 11]}>
                        <Voucher {...voucher} />
                    </Col>
                </Grid>
            </Section>
            <CampusTour {...campusTour} />
            <ContactUs {...contactUs} />
        </Main>
    );
};

export default Landing;
